import { Box, Flex, Spacer } from "@chakra-ui/react";
import { RefObject } from "react";
import { useIntl } from "react-intl";

import { AnimateSize } from "@nens/chakra/components";
import { CommentRead } from "@nens/rana/api-v1";
import { RanaForm, SendIcon } from "@nens/ranalib";

import { RESOLVED_OPACITY } from "../consts/resolved-opacity";
import { UsePublicationCommentReturn } from "../hooks/use-comment";

import { CommentHeader } from "./comment-components";
import { CommentList } from "./comment-list";

const PADDING = 2;
const THREAD_STARTER_INDEX = 0;

interface PublicationCommentThreadProps {
  selectedCommentThread: CommentRead;
  addCommentToThread: UsePublicationCommentReturn["addCommentToThread"];
  editCommentMessage: UsePublicationCommentReturn["editCommentMessage"];
  removeCommentMessage: UsePublicationCommentReturn["removeCommentMessage"];
  removeCommentThread: UsePublicationCommentReturn["removeCommentThread"];
  resolveComment: UsePublicationCommentReturn["resolveComment"];
  toggleCommentReaction: UsePublicationCommentReturn["toggleCommentReaction"];
  /** Ref to an element that should not trigger the outside-click close handler. */
  excludeRef?: RefObject<HTMLElement | null>;
}

export function PublicationCommentThread({
  selectedCommentThread,
  addCommentToThread,
  editCommentMessage,
  removeCommentMessage,
  removeCommentThread,
  resolveComment,
  toggleCommentReaction,
}: PublicationCommentThreadProps) {
  const intl = useIntl();
  const showInput = !selectedCommentThread.resolved;
  const threadId = selectedCommentThread.id;

  const editThreadStarter = (body: string) =>
    editCommentMessage({
      commentId: threadId,
      messageIndex: THREAD_STARTER_INDEX,
      body,
    });

  const toggleThreadStarterReaction = (emoji: string) =>
    toggleCommentReaction({
      commentId: threadId,
      messageIndex: THREAD_STARTER_INDEX,
      emoji,
    });

  return (
    <Box borderRadius="base" minW="2xs" maxW="sm" maxH="lg" display="flex" flexDirection="column">
      <Box py={PADDING}>
        <CommentHeader
          resolved={selectedCommentThread.resolved}
          resolveMessage={() =>
            resolveComment({
              commentId: selectedCommentThread.id,
              type: selectedCommentThread.resolved ? "unresolve" : "resolve",
            })
          }
          editMessage={(body: string) => editThreadStarter(body)}
          removeMessage={() => removeCommentThread(selectedCommentThread.id)}
          message={selectedCommentThread.messages[THREAD_STARTER_INDEX]}
          onToggleReaction={(emoji) => toggleThreadStarterReaction(emoji)}
          mb={4}
        />

        <AnimateSize
          opacity={selectedCommentThread.resolved ? RESOLVED_OPACITY : undefined}
          overflow="auto"
          maxH="2xs"
          w="full"
          defaultToBottom
        >
          <CommentList
            {...{ editCommentMessage, removeCommentMessage, selectedCommentThread }}
            toggleCommentReaction={toggleCommentReaction}
          />
        </AnimateSize>
      </Box>

      <Spacer />

      {showInput && (
        <RanaForm
          initialValues={{ comment: "" }}
          emptyOnSubmit
          doSubmit={({ comment }) => {
            if (!comment) return;
            if (selectedCommentThread) {
              addCommentToThread({ threadId: selectedCommentThread.id, comment: comment + "" });
            }
          }}
        >
          <Flex py={PADDING} gap={PADDING}>
            <RanaForm.Input
              explanation={intl.formatMessage({
                id: "respond",
                defaultMessage: "Respond",
              })}
              submitOnEnter
              autoFocus
              field="comment"
              mb={0}
            />
            <RanaForm.Errors />
            <RanaForm.SubmitButton w="button">
              <SendIcon />
            </RanaForm.SubmitButton>
          </Flex>
        </RanaForm>
      )}
    </Box>
  );
}
